import { getReviews } from '@cinch-nx/data-trustpilot'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'

import { getHomePageContentData } from './data/content'
import { getHomePageVehiclesData } from './data/vehicles'
import { getPersonalisationEligibility } from './helpers/get-personalisation-eligibility'

import { setTag, traceFunction } from '@core/datadog-server'
import { getAdobeExperimentSSR } from '@cinch-nx/adobe-server-side'
import { Env, readFromEnv } from '@cinch-nx/environments'

const EXPERIMENT_NAMES = [readFromEnv(Env.NavbarValuationExperimentId)]

async function getServerSidePropsfn(ctx: GetServerSidePropsContext) {
  const isInsideStoryblok = !!ctx.preview

  const [
    { vehiclesData, response: searchResponse },
    story,
    trustpilotData,
    { showPersonalisedComponents, similarListings, recentlyViewedListings },
    experiments,
  ] = await Promise.all([
    getHomePageVehiclesData(),
    getHomePageContentData(isInsideStoryblok),
    getReviews(),
    getPersonalisationEligibility(ctx.req.cookies),
    getAdobeExperimentSSR(EXPERIMENT_NAMES, ctx),
  ])

  if (!story) {
    throw new Error('Unable to fetch `home` story from storyblok')
  }

  setTag('response.code', 200)
  setTag('page.name', 'homepage')
  setTag('page.url', ctx.resolvedUrl)
  setTag('page.rendered', 'SSR')
  setTag(
    'page.params',
    JSON.stringify(
      {
        preview: isInsideStoryblok,
        query: ctx.query,
        showPersonalisedComponents,
      },
      null,
      2,
    ),
  )

  return {
    props: {
      story,
      vehiclesData,
      searchResponse,
      showPersonalisedComponents,
      trustpilotData,
      preview: isInsideStoryblok,
      recentlyViewedListings,
      similarListings,
      generatedAt: Date.now().toString(),
      layoutProps: {
        hasPromoBanner: false,
      },
      pageMeta: {
        experiments,
      },
    },
  }
}

export const getServerSideProps = traceFunction(
  'homepage.getServerSideProps',
  getServerSidePropsfn,
)

// infer the type, but make searchResponse optional
export type HomepageProps = Omit<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  'searchResponse'
> &
  Partial<
    Pick<
      InferGetServerSidePropsType<typeof getServerSideProps>,
      'searchResponse'
    >
  >
